'use client';

import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  headingLevel,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Two call-to-action cards side by side, e.g. "Plan your kitchen online" and
 * "Book a design consultation".
 *
 * <p>Tones: `sage` is white text on the sage surface (4.53:1, AA), `stone` is
 * ink on stone (8.8:1), `warm` is ink on the warm surface. The sage tone uses
 * a light button so the button stays distinguishable from its own card.
 */
export interface SpatialKitchenCtaCard {
  eyebrow?: Localized;
  title?: Localized;
  body?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  tone?: 'sage' | 'stone' | 'warm';
  image?: SkImageRef;
}

export interface SpatialKitchenCtaPairProps {
  /** Visually hidden section heading, for screen-reader navigation. */
  title?: Localized;
  cards?: SpatialKitchenCtaCard[];
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-ctapair__grid{display:grid;gap:1.25rem}
@media (min-width:769px){.sk-ctapair__grid{grid-template-columns:repeat(auto-fit,minmax(min(100%,17rem),1fr));gap:clamp(1.25rem,2vw,2rem)}}
.sk-ctacard{display:grid;grid-template-rows:auto 1fr;border-radius:8px;overflow:hidden;min-height:100%}
.sk-ctacard__media{aspect-ratio:16/9}
.sk-ctacard__body{display:grid;align-content:start;gap:1rem;padding:clamp(1.75rem,1.25rem + 2vw,3rem)}
.sk-ctacard__title{font-size:clamp(1.75rem,1.4rem + 1.2vw,2.375rem)}
.sk-ctacard__action{margin-top:.5rem}
.sk-ctacard--sage{background:var(--sk-sage);color:#fff}
.sk-ctacard--sage .sk-heading,.sk-ctacard--sage .sk-body,.sk-ctacard--sage .sk-eyebrow{color:#fff}
.sk-ctacard--stone{background:var(--sk-stone)}
.sk-ctacard--stone .sk-body,.sk-ctacard--stone .sk-eyebrow{color:var(--sk-ink)}
.sk-ctacard--warm{background:var(--sk-warm)}
`;

export function SpatialKitchenCtaPair(props: SpatialKitchenCtaPairProps) {
  const cards = items<SpatialKitchenCtaCard>(props.cards).slice(0, 3);
  const title = text(props.title, 'Ways to start');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = level === 'h1' ? 'h2' : 'h3';

  return (
    <BlockRoot as="section" className={`sk sk-ctapair sk-section ${surfaceClass(props.surface, 'white')}`}>
      <SkBaseStyles />
      <SkStyles id="cta-pair" css={CSS} />
      <div className="sk-container">
        <Title className="sk-visually-hidden">{title}</Title>
        <div className="sk-ctapair__grid">
          {cards.map((card, index) => {
            const tone = card.tone === 'stone' || card.tone === 'warm' ? card.tone : 'sage';
            const cardTitle = text(card.title);
            const ctaLabel = text(card.ctaLabel);
            return (
              <article key={`${cardTitle}-${index}`} className={`sk-ctacard sk-ctacard--${tone}`}>
                {card.image?.src ? (
                  <div className="sk-ctacard__media sk-media">
                    <SkImage image={card.image} sizes="(min-width: 769px) 50vw, 100vw" />
                  </div>
                ) : (
                  <div aria-hidden="true" />
                )}
                <div className="sk-ctacard__body">
                  {text(card.eyebrow) && <p className="sk-eyebrow">{text(card.eyebrow)}</p>}
                  {cardTitle && <CardTitle className="sk-heading sk-ctacard__title">{cardTitle}</CardTitle>}
                  {text(card.body) && <p className="sk-body">{text(card.body)}</p>}
                  {ctaLabel && card.ctaHref && (
                    <p className="sk-ctacard__action" style={{ margin: 0 }}>
                      <SkLink
                        href={card.ctaHref}
                        className={`sk-btn ${tone === 'sage' ? 'sk-btn--light' : 'sk-btn--primary'}`}
                      >
                        {ctaLabel}
                        <Icon name="arrow-right" size={16} />
                      </SkLink>
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </BlockRoot>
  );
}
